<template>
  <transition name="app-dialog-fade">
    <div
      v-if="visible"
      ref="root"
      class="app-dialog"
      role="dialog"
      aria-modal="true"
      :aria-labelledby="titleId"
      tabindex="-1"
      @mousedown.self="onBackdropMousedown"
    >
      <div class="app-dialog__panel" role="document" @mousedown.stop>
        <header class="app-dialog__head">
          <span :class="['app-dialog__icon', `app-dialog__icon--${variant}`]" aria-hidden="true">
            <svg v-if="variant === 'warning'" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
              <line x1="12" y1="9" x2="12" y2="13"/>
              <line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
            <svg v-else-if="variant === 'danger'" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="8" x2="12" y2="12"/>
              <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            <svg v-else-if="variant === 'success'" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
            <svg v-else viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="16" x2="12" y2="12"/>
              <line x1="12" y1="8" x2="12.01" y2="8"/>
            </svg>
          </span>
          <div class="app-dialog__heading">
            <h3 :id="titleId">{{ title }}</h3>
            <p v-if="message">{{ message }}</p>
          </div>
        </header>

        <div v-if="$slots.default" class="app-dialog__body">
          <slot />
        </div>

        <footer class="app-dialog__foot">
          <button
            v-if="cancelLabel"
            type="button"
            class="app-dialog__btn app-dialog__btn--ghost"
            :disabled="busy"
            @click="onCancel"
          >
            {{ cancelLabel }}
          </button>
          <button
            v-if="discardLabel"
            type="button"
            class="app-dialog__btn app-dialog__btn--danger"
            :disabled="busy"
            @click="onDiscard"
          >
            {{ discardLabel }}
          </button>
          <button
            v-if="confirmLabel"
            type="button"
            class="app-dialog__btn app-dialog__btn--primary"
            :class="{ 'app-dialog__btn--busy': busy }"
            :disabled="busy"
            @click="onConfirm"
          >
            <span v-if="busy" class="app-dialog__spinner" aria-hidden="true"></span>
            <span>{{ busy && busyLabel ? busyLabel : confirmLabel }}</span>
          </button>
        </footer>
      </div>
    </div>
  </transition>
</template>

<script>
let dialogIdCounter = 0

export default {
  name: 'AppConfirmDialog',
  props: {
    visible: { type: Boolean, default: false },
    title: { type: String, required: true },
    message: { type: String, default: '' },
    confirmLabel: { type: String, default: 'Confirm' },
    discardLabel: { type: String, default: '' },
    cancelLabel: { type: String, default: 'Cancel' },
    busy: { type: Boolean, default: false },
    busyLabel: { type: String, default: '' },
    variant: {
      type: String,
      default: 'warning',
      validator: (v) => ['info', 'success', 'warning', 'danger'].includes(v)
    },
    closeOnBackdrop: { type: Boolean, default: true }
  },
  data () {
    dialogIdCounter += 1
    return {
      titleId: `app-dialog-title-${dialogIdCounter}`
    }
  },
  watch: {
    visible (now) {
      if (now) {
        this.$nextTick(() => {
          this.attachPanel()
          this.lockBodyScroll()
          if (this.$refs.root) this.$refs.root.focus()
        })
        document.addEventListener('keydown', this.onKeydown)
      } else {
        document.removeEventListener('keydown', this.onKeydown)
        this.unlockBodyScroll()
      }
    }
  },
  beforeDestroy () {
    document.removeEventListener('keydown', this.onKeydown)
    this.unlockBodyScroll()
    this.detachPanel()
  },
  methods: {
    attachPanel () {
      const root = this.$refs.root
      if (root && root.parentNode !== document.body) {
        document.body.appendChild(root)
      }
    },
    detachPanel () {
      const root = this.$refs.root
      if (root && root.parentNode === document.body) {
        document.body.removeChild(root)
      }
    },
    lockBodyScroll () {
      this._prevBodyOverflow = document.body.style.overflow
      document.body.style.overflow = 'hidden'
    },
    unlockBodyScroll () {
      if (this._prevBodyOverflow !== undefined) {
        document.body.style.overflow = this._prevBodyOverflow
        this._prevBodyOverflow = undefined
      }
    },
    onKeydown (event) {
      if (event.key === 'Escape' && !this.busy) {
        event.preventDefault()
        this.onCancel()
      }
    },
    onBackdropMousedown () {
      if (!this.closeOnBackdrop || this.busy) return
      this.onCancel()
    },
    onConfirm () {
      this.$emit('confirm')
    },
    onDiscard () {
      this.$emit('discard')
    },
    onCancel () {
      this.$emit('cancel')
      this.$emit('update:visible', false)
    }
  }
}
</script>

<style scoped>
.app-dialog {
  position: fixed;
  inset: 0;
  z-index: 1100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.25rem;
  background: rgba(15, 23, 42, 0.45);
  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);
  outline: none;
}

.app-dialog__panel {
  width: 100%;
  max-width: 460px;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 14px;
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.28), 0 2px 6px rgba(15, 23, 42, 0.08);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.app-dialog__head {
  display: flex;
  align-items: flex-start;
  gap: 0.85rem;
  padding: 1.1rem 1.15rem 0.6rem;
}

.app-dialog__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  flex-shrink: 0;
}

.app-dialog__icon--warning { background: #fef3c7; color: #b45309; }
.app-dialog__icon--danger  { background: #fee2e2; color: #b91c1c; }
.app-dialog__icon--success { background: #dff5ea; color: #0e5b46; }
.app-dialog__icon--info    { background: #dbeafe; color: #1e40af; }

.app-dialog__heading {
  flex: 1;
  min-width: 0;
}

.app-dialog__heading h3 {
  margin: 0 0 0.2rem;
  font-size: 1rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
  line-height: 1.25;
}

.app-dialog__heading p {
  margin: 0;
  font-size: 0.82rem;
  color: #4b5563;
  line-height: 1.45;
}

.app-dialog__body {
  padding: 0 1.15rem 0.6rem;
  font-size: 0.85rem;
  color: #374151;
}

.app-dialog__foot {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
  padding: 0.85rem 1.15rem 1rem;
  flex-wrap: wrap;
}

.app-dialog__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  height: 36px;
  padding: 0 1rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.83rem;
  letter-spacing: 0.01em;
  border: 1px solid transparent;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease, border-color 0.15s ease, background 0.15s ease, color 0.15s ease;
  font-family: inherit;
}

.app-dialog__btn:disabled {
  cursor: not-allowed;
  opacity: 0.7;
}

.app-dialog__btn--ghost {
  background: #ffffff;
  border-color: #e5e9f2;
  color: #1f2a44;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.app-dialog__btn--ghost:hover:not(:disabled) {
  border-color: #b9c2d0;
  background: #f8fafc;
}

.app-dialog__btn--danger {
  background: #ffffff;
  border-color: #fecaca;
  color: #b91c1c;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.app-dialog__btn--danger:hover:not(:disabled) {
  background: #fef2f2;
  border-color: #ef4444;
  color: #991b1b;
}

.app-dialog__btn--primary {
  background: var(--brand-gradient, linear-gradient(135deg, #0e5b46 0%, #16a07a 100%));
  border-color: transparent;
  color: #ffffff;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.22);
}

.app-dialog__btn--primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 14px rgba(14, 91, 70, 0.3);
  filter: brightness(1.05);
  color: #ffffff;
}

.app-dialog__btn--primary:active:not(:disabled) {
  transform: translateY(0);
  filter: brightness(0.96);
}

.app-dialog__btn--busy {
  cursor: progress;
}

.app-dialog__spinner {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: #ffffff;
  border-radius: 50%;
  animation: app-dialog-spin 0.7s linear infinite;
}

@keyframes app-dialog-spin {
  to { transform: rotate(360deg); }
}

.app-dialog-fade-enter-active,
.app-dialog-fade-leave-active {
  transition: opacity 0.15s ease;
}

.app-dialog-fade-enter-active .app-dialog__panel,
.app-dialog-fade-leave-active .app-dialog__panel {
  transition: transform 0.18s ease;
}

.app-dialog-fade-enter,
.app-dialog-fade-leave-to {
  opacity: 0;
}

.app-dialog-fade-enter .app-dialog__panel,
.app-dialog-fade-leave-to .app-dialog__panel {
  transform: translateY(10px) scale(0.985);
}

@media (max-width: 480px) {
  .app-dialog {
    padding: 0.75rem;
    align-items: flex-end;
  }

  .app-dialog__panel {
    max-width: 100%;
    border-radius: 14px;
  }

  .app-dialog__foot {
    flex-direction: column-reverse;
    align-items: stretch;
  }

  .app-dialog__btn {
    width: 100%;
  }
}
</style>
